import type { StoredConsent } from 'src/utils/storedCookieConsent'
import config from '../config'

// A vendor's consent DEFAULT, from the visitor's saved banner choice. It has
// to be in place before the vendor's first hit — CookieConsent's own `update`
// runs later, from a lazily loaded chunk, so a visitor who had rejected was
// still measured as granted on every landing. No saved choice = granted.
const consentState = (allowed: boolean | undefined): 'granted' | 'denied' =>
  allowed === false ? 'denied' : 'granted'

// Meta pixel library only. The `fbq` stub, base `init` and stored consent come
// from src/utils/pixels/fbp.ts (installed on first use), so calls made before
// <Scripts /> loads this on its 2 s fuse queue instead of being dropped.
const FacebookPixelSrc = 'https://connect.facebook.net/en_US/fbevents.js'

const gtagScript = (consent: StoredConsent | null): string => `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('consent', 'default', {
  ad_storage: '${consentState(consent?.advertisement)}',
  ad_user_data: '${consentState(consent?.advertisement)}',
  ad_personalization: '${consentState(consent?.advertisement)}',
  analytics_storage: '${consentState(consent?.analytics)}',
  functionality_storage: '${consentState(consent?.functionality)}',
  personalization_storage: 'granted',
  security_storage: 'granted'
});
gtag('js', new Date());
gtag('config', '${config.pixels.ga}', { page_path: window.location.pathname });
window.gtag = gtag;
`

const LinkedInScript1 = `
_linkedin_partner_id = "7246484";
window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
window._linkedin_data_partner_ids.push(_linkedin_partner_id);
`

const LinkedInScript2 = `
(function(l) {
if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
window.lintrk.q=[]}
var s = document.getElementsByTagName("script")[0];
var b = document.createElement("script");
b.type = "text/javascript";b.async = true;
b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
s.parentNode.insertBefore(b, s);})(window.lintrk);`

const ConsoleMessage = `
%c👋 Welcome to SpotMe! 👋

%c   *****    *****
  ******* ********
 ******************
  ****************
   *************
    **********
      ******
        **

%c🏠 Store Anywhere, Anytime with SpotMe! 🏠

👩‍💻 Are you a developer? We're hiring! 
🔗 Join our team: https://www.linkedin.com/company/spotmemx
🌐 Visit us: https://www.spotme.mx
📧 Contact: hola@spotme.mx

%c💡 "Transforming the way people store their belongings" 💡
`

// Microsoft Clarity was removed 2026-05-13 in favor of PostHog (session
// replay + product analytics + MCP server). PostHog is initialized from
// `src/utils/pixels/posthog.ts` and triggered inside `Scripts.tsx` —
// there is no inline `<script>` snippet to maintain here. Bing UET
// (a separate Microsoft product for ad-conversion tracking) stays —
// see `microsoftScript` below and `src/utils/pixels/uetq.ts`.
// Note: "Looker Studio" used to read from GA4 only; it never had a
// frontend pixel, so nothing to remove there.

// The `if(typeof UET==="undefined")return;` guard below is OURS, not
// Microsoft's. `bat.js` fires onload even when a content blocker or an
// in-app WebView proxy serves it as an empty/stubbed body, so `new UET(o)`
// threw an unhandled `ReferenceError: UET is not defined` into Datadog
// (issue d922707e, 2026-08-30, Chrome Mobile WebView). Nothing is lost by
// returning early: with no `UET` there is nothing to initialize either way.
// Keep the guard if this snippet is ever re-copied from Microsoft's docs.
const microsoftScript = (consent: StoredConsent | null): string => `
window.uetq = window.uetq || [];
window.uetq.push("consent", "default", { ad_storage: "${consentState(consent?.advertisement)}" });
(function(w,d,t,r,u)
{
  var f,n,i;
  w[u]=w[u]||[],f=function()
  {
    if(typeof UET==="undefined")return;
    var o={ti:"187206660", enableAutoSpaTracking: true};
    o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")
  },
  n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function()
  {
    var s=this.readyState;
    s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)
  },
  i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)
})
(window,document,"script","//bat.bing.com/bat.js","uetq");`

const OpenAiScript = `
!function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)}(window,document,"script","https://bzrcdn.openai.com/sdk/oaiq.min.js");oaiq("init",{pixelId:"${config.pixels.openai}"});`

export {
  ConsoleMessage,
  FacebookPixelSrc,
  gtagScript,
  LinkedInScript1,
  LinkedInScript2,
  microsoftScript,
  OpenAiScript
}
